import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../auth";

export type LinkItem = { label: string; href: string; imageUrl?: string };

type Props = {
  onClose: () => void;
  adminPw: string;
  setAdminPw?: (pw: string) => void;
};

const LinksAdmin: React.FC<Props> = ({ onClose, adminPw, setAdminPw }) => {
  const { isLoggedIn, token, user } = useAuth();
  const [items, setItems] = useState<LinkItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [label, setLabel] = useState("");
  const [href, setHref] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [busy, setBusy] = useState(false);

  const canSubmit = useMemo(() => {
    return label.trim().length > 0 && /^https?:\/\//i.test(href.trim());
  }, [label, href]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/links", { headers: { accept: "application/json" } });
      if (!res.ok) throw new Error(`Failed to load links (${res.status})`);
      const data = await res.json();
      setItems(Array.isArray(data?.links) ? data.links : []);
    } catch (err: any) {
      setError(err?.message || "Failed to load links");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function addLink() {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch("/api/links", {
        method: "POST",
        headers: { "content-type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ label: label.trim(), href: href.trim(), imageUrl: imageUrl.trim(), password: adminPw }),
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `Failed (${res.status})`);
      }
      const data = await res.json();
      setItems(Array.isArray(data?.links) ? data.links : []);
      setLabel("");
      setHref("");
      setImageUrl("");
    } catch (err: any) {
      setError(err?.message || "Failed to add link");
    } finally {
      setBusy(false);
    }
  }

  async function removeLink(targetHref: string) {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch("/api/links", {
        method: "DELETE",
        headers: { "content-type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ href: targetHref, password: adminPw }),
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `Failed (${res.status})`);
      }
      const data = await res.json();
      setItems(Array.isArray(data?.links) ? data.links : []);
    } catch (err: any) {
      setError(err?.message || "Failed to remove link");
    } finally {
      setBusy(false);
    }
  }

  async function updateLink(oldHref: string, newLabel: string, newHref: string, newImageUrl: string) {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch("/api/links", {
        method: "PUT",
        headers: { "content-type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ href: oldHref, newLabel, newHref, newImageUrl, password: adminPw }),
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `Failed (${res.status})`);
      }
      const data = await res.json();
      setItems(Array.isArray(data?.links) ? data.links : []);
    } catch (err: any) {
      setError(err?.message || "Failed to update link");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="links-modal" role="dialog" aria-modal="true" aria-label="Manage Websites">
      <div className="links-modal__content">
        <div className="links-modal__header">
          <h2 style={{ margin: 0 }}>Manage Websites</h2>
          <button className="btn" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="links-modal__body">
          {!isLoggedIn && (
            <div className="pw-row">
              <label className="label">Admin Password</label>
              <input
                type="password"
                value={adminPw}
                onChange={(e) => {
                  setAdminPw?.(e.target.value);
                  try {
                    localStorage.setItem("blog_entry_password", e.target.value);
                  } catch {}
                }}
                className="input"
                placeholder="Enter password or sign in"
              />
            </div>
          )}
          {isLoggedIn && <div style={{ margin: "4px 0 10px", fontWeight: 800 }}>Signed in as {user?.email || user?.name}</div>}

          <div className="add-row">
            <div>
              <label className="label">Label</label>
              <input className="input" type="text" value={label} onChange={(e) => setLabel(e.target.value)} />
            </div>
            <div>
              <label className="label">URL</label>
              <input className="input" type="url" value={href} onChange={(e) => setHref(e.target.value)} placeholder="https://..." />
            </div>
            <div>
              <label className="label">Logo URL</label>
              <input className="input" type="url" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://... (optional)" />
            </div>
            <div style={{ alignSelf: "end" }}>
              <button className="btn btn-primary" onClick={addLink} disabled={!canSubmit || busy}>
                Add Link
              </button>
            </div>
          </div>

          {error && <div className="error">{error}</div>}
          {loading ? (
            <div>Loading...</div>
          ) : (
            <ul className="links-list">
              {items.map((it) => (
                <EditableLinkRow key={it.href} item={it} busy={busy} onRemove={() => removeLink(it.href)} onSave={(lbl, url, imgUrl) => updateLink(it.href, lbl, url, imgUrl)} />
              ))}
            </ul>
          )}
        </div>
      </div>

      <style>
        {`
          .links-modal {
            position: fixed;
            inset: 0;
            background: rgba(0,0,0,0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 3500;
            padding: 24px;
          }
          .links-modal__content {
            width: min(900px, 100%);
            background: #fff;
            border: 6px solid #000;
            box-shadow: 10px 10px 0 #000;
            padding: 16px;
            display: flex;
            flex-direction: column;
            max-height: calc(100vh - 48px);
            overflow: hidden;
          }
          .links-modal__header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
          .links-modal__body { flex: 1 1 auto; min-height: 0; overflow: auto; }
          .label { font-weight: 800; display: block; margin-bottom: 6px; }
          .input { border: 3px solid #000; box-shadow: 4px 4px 0 #000; background: #fff; padding: 8px 10px; font-weight: 700; width: 100%; }
          .btn { border: 3px solid #000; box-shadow: 3px 3px 0 #000; background: #fff; padding: 8px 12px; font-weight: 800; cursor: pointer; }
          .btn-primary { background: #000; color: #fff; }
          .btn-danger { background: #b00020; color: #fff; }
          .add-row { display: grid; grid-template-columns: 1fr 1fr 1fr auto; gap: 10px; align-items: end; margin: 10px 0 16px; }
          .pw-row { display: grid; grid-template-columns: auto 1fr; gap: 8px; align-items: center; margin: 4px 0 12px; }
          .links-list { list-style: none; padding: 0; margin: 0; display: grid; gap: 8px; }
          .row { display: grid; grid-template-columns: 1fr 1fr 1fr auto auto; gap: 8px; align-items: center; border: 3px solid #000; box-shadow: 5px 5px 0 #000; padding: 10px; background: #fff; }
          .row .field { width: 100%; }
          .error { color: #b00020; margin: 8px 0; }
          @media (max-width: 760px) { .add-row, .row { grid-template-columns: 1fr; } }
        `}
      </style>
    </div>
  );
};

const EditableLinkRow: React.FC<{
  item: LinkItem;
  busy?: boolean;
  onRemove: () => void;
  onSave: (label: string, href: string, imageUrl: string) => void;
}> = ({ item, busy, onRemove, onSave }) => {
  const [label, setLabel] = useState(item.label);
  const [href, setHref] = useState(item.href);
  const [imageUrl, setImageUrl] = useState(item.imageUrl || "");
  const changed = label !== item.label || href !== item.href || imageUrl !== (item.imageUrl || "");
  const valid = label.trim().length > 0 && /^https?:\/\//i.test(href.trim());
  return (
    <li className="row">
      <input className="input field" value={label} onChange={(e) => setLabel(e.target.value)} />
      <input className="input field" value={href} onChange={(e) => setHref(e.target.value)} />
      <input className="input field" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="Logo URL (optional)" />
      <button className="btn" onClick={() => onSave(label.trim(), href.trim(), imageUrl.trim())} disabled={!changed || !valid || busy}>
        Save
      </button>
      <button className="btn btn-danger" onClick={onRemove} disabled={busy}>
        Delete
      </button>
    </li>
  );
};

export default LinksAdmin;
